<template>
  <div>
    <div
      aria-label="Activity Letters with wood pieces"
      class="activity-stage wood-pieces"
      id="activity-boundary"
      style="visibility:hidden;height: 100%;overflow: auto"
    >
      <div id="dragging-container">
        <div style="position:relative;">
          <div class="woods-container" ref="woodsContainer"></div>
          <div class="exercise-woods-container" ref="exerciseWoodsContainer"></div>
        </div>
        <div class="letters-container">
          <div class="sample-container" ref="sampleContainer"></div>
          <div class="exercise-container" ref="exerciseContainer"></div>
        </div>
      </div>
      <div
        role="region"
        aria-live="assertive"
        ref="hiddenDiv"
        style="width: 1px; height: 1px; z-index: -1000; position: absolute; color:transparent"
      >
        {{ message }}
      </div>
    </div>
    <caption-reader v-if="accessibilityMode" ref="playerInstance"></caption-reader>
  </div>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';
import Draggable from 'gsap/Draggable';
import { mapState, mapMutations } from 'vuex';

import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';
import PlayerManager from 'mixins/PlayerManager';

import { hasTouchedBoundings, getActivityBoundings } from 'utils/activity-util';

import littleCurve from 'assets/images/activities/wood-pieces/littleCurve.png';
import littleLine from 'assets/images/activities/wood-pieces/littleLine.png';
import bigCurve from 'assets/images/activities/wood-pieces/bigCurve.png';
import bigLine from 'assets/images/activities/wood-pieces/bigLine.png';
import smiley from 'assets/images/activities/wood-pieces/smiley.png';
import Player from 'src/components/utils/Player.vue';

export default {
  mixins: [ActivityManager, ErrorLogger, TypingActivityHelper, PlayerManager],
  components: {
    'caption-reader': Player
  },
  data() {
    return {
      activityName: 'WoodPieces',
      ctHeight: 360,
      ctWidth: 270,
      currentShape: 0,
      message: '',
      sfx: [],
      soundsBucket: {},
      captionsBucket: {},
      draggableInstances: [],
      selectedStrokeNr: null,
      selectedStroke: null
    };
  },

  computed: {
    ...mapState('application', {
      isLiteVersion: state => state.isLiteVersion,
      accessibilityMode: state => state.accessibilityMode
    }),
    ...mapState('accessibility', {
      soundMuted: state => state.soundMuted,
      soundVolume: state => state.soundVolume
    })
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown);
    TweenMax.killAll();
    Howler.volume(1); // eslint-disable-line no-undef
  },

  methods: {
    ...mapMutations('accessibility', [
      'setOnGoingAnimation',
      'setCurrentTweenData',
      'setAnimationsMode'
    ]),
    async loadActivity() {
      this.createRounds(this.getActivityState());
      this.setupNewRound();
      this.sfx = await this.loadAssets();
      [, this.soundsBucket, this.captionsBucket] = this.sfx;
    },

    async loadImages() {
      this.queueImage(littleLine);
      this.queueImage(bigLine);
      this.queueImage(littleCurve);
      this.queueImage(bigCurve);
      this.queueImage(smiley);
      this.downloadImages();
    },

    async loadSounds() {
      const stepAudioPaths = [];
      const lineAudioPaths = {};
      const general = {};

      this.rounds.forEach(round => {
        round.steps.forEach(step => {
          Object.assign(lineAudioPaths, { [step.stroke]: step.audioAsset.src });

          this.queueAudio(lineAudioPaths[step.stroke]);
        });
      });

      this.rounds.forEach(
        ({ completedAudio, instructionAudio, nowItsYourTurn, congratulations }) => {
          this.queueAudio(instructionAudio.src);
          this.queueAudio(completedAudio.src);
          this.queueAudio(nowItsYourTurn.src);
          this.queueAudio(congratulations.src);
          stepAudioPaths.push({
            instructionAudio,
            completedAudio
          });
          Object.assign(general, {
            generalCongrats: congratulations.src,
            nowitsyourturn: nowItsYourTurn.src
          });
        }
      );

      const cache = await this.downloadAudio();

      return {
        soundsPerStep: stepAudioPaths.map(audioPaths => ({
          instructionAudio: cache.get(audioPaths.instructionAudio.src),
          completedAudio: cache.get(audioPaths.completedAudio.src)
        })),
        lineSounds: Object.keys(lineAudioPaths).map(key => ({
          [key]: cache.get(lineAudioPaths[key])
        })),
        general: {
          generalCongrats: cache.get(general.generalCongrats),
          nowitsyourturn: cache.get(general.nowitsyourturn)
        }
      };
    },

    async loadCaptions() {
      const stepTextPaths = [];
      const lineTextPaths = {};
      const general = {};

      this.rounds.forEach(round => {
        round.steps.forEach(step => {
          Object.assign(lineTextPaths, { [step.stroke]: step.audioAsset.captions });
          this.queueCaptions(lineTextPaths[step.stroke]);
        });
      });

      this.rounds.forEach(
        ({ completedAudio, instructionAudio, nowItsYourTurn, congratulations }) => {
          this.queueCaptions(instructionAudio.captions);
          this.queueCaptions(completedAudio.captions);
          this.queueCaptions(nowItsYourTurn.captions);
          this.queueCaptions(congratulations.captions);
          stepTextPaths.push({
            instructionAudio,
            completedAudio
          });
          Object.assign(general, {
            generalCongrats: congratulations.captions,
            nowitsyourturn: nowItsYourTurn.captions
          });
        }
      );

      const cache = await this.downloadCaptions();

      return {
        textPerStep: stepTextPaths.map(textPaths => ({
          instructionAudio: cache.get(textPaths.instructionAudio.captions),
          completedAudio: cache.get(textPaths.completedAudio.captions)
        })),
        lineText: Object.keys(lineTextPaths).map(key => ({
          [key]: cache.get(lineTextPaths[key])
        })),
        general: {
          generalCongrats: cache.get(general.generalCongrats),
          nowitsyourturn: cache.get(general.nowitsyourturn)
        }
      };
    },
    playSound(sound, caption, accessibilityMode) {
      if (accessibilityMode) {
        const { playerInstance } = this.$refs;
        playerInstance.playSoundAndShowCaptions(sound, caption);
        return;
      }
      sound.play();
    },
    playStepInstructions() {
      const { soundsBucket, captionsBucket, accessibilityMode } = this;
      const currentSound =
        soundsBucket.soundsPerStep[this.currentRound.roundIndex].instructionAudio;
      const currentCaption =
        captionsBucket.textPerStep[this.currentRound.roundIndex].instructionAudio;
      this.playSound(currentSound, currentCaption, accessibilityMode);
    },
    playYourTurn() {
      const { soundsBucket, captionsBucket, accessibilityMode } = this;
      this.playSound(
        soundsBucket.general.nowitsyourturn,
        captionsBucket.general.nowitsyourturn,
        accessibilityMode
      );
    },
    playStepCongrats() {
      const { soundsBucket, captionsBucket, accessibilityMode } = this;
      const stepSound = soundsBucket.soundsPerStep[this.currentRound.roundIndex].completedAudio;
      const stepCaption = captionsBucket.textPerStep[this.currentRound.roundIndex].completedAudio;
      this.playSound(stepSound, stepCaption, accessibilityMode);
    },
    playShapeInstructions() {
      this.sfx.lineSounds[this.currentStep.stroke].play();
    },

    async loadAssets() {
      const resolutions = await Promise.all([
        this.loadImages(),
        this.loadSounds(),
        this.loadCaptions()
      ]);

      return resolutions;
    },

    getActivityState() {
      const { rounds } = this.activityData;
      return {
        rounds,
        instructions: {
          voice: [''],
          delay: 4
        }
      };
    },

    startActivity() {
      this.errorLoggerStart();
      this.startDemo();
    },

    startDemo() {
      const self = this;
      // play instruction sound for current letter
      this.playStepInstructions();
      // wait for instructions to finish => add delay
      TweenMax.delayedCall(2.5, () => {
        self.adjustContainers();
        TweenMax.to('.activity-stage', 2, { css: { autoAlpha: 1 } });
        TweenMax.set('.sample-container', { css: { x: 150, autoAlpha: 1 } });
        TweenMax.set('.exercise-container', { css: { autoAlpha: 0 } });
        self.injectDemoShapes();
        self.setSmiley('sample-container');
        self.setSmiley('exercise-container');
      });
    },

    handleAriaLive(text) {
      // const { hiddenDiv } = this.$refs;
      // hiddenDiv.style.display = 'block';
      this.message = text;
      setTimeout(() => {
        this.message = ' ';
        //  hiddenDiv.style.display = 'none';
      }, 500);
    },

    drawDroppableArea(shape, i, img, theLabel) {
      const getLabel = this.getLabel(shape.stroke);

      const droppableCt = document.createElement('button');

      droppableCt.setAttribute('id', `droppableContainer-${i}`);
      droppableCt.setAttribute('class', 'droppableContainer');
      droppableCt.setAttribute('aria-pressed', false);
      droppableCt.setAttribute('aria-label', theLabel(getLabel));
      Object.assign(droppableCt.style, {
        position: 'absolute',
        transform: `rotate(${shape.position.r}deg)`,
        width: `${img.naturalWidth * this.scaleParam}px`,
        height: `${img.naturalHeight * this.scaleParam}px`,
        left: `${shape.position.x}px`,
        top: `${shape.position.y}px`,
        transformOrigin: '0% 0%',
        background: 'transparent',
        border: 'none'
      });

      droppableCt.addEventListener('keydown', this.handleDropWithKeyboard);

      return droppableCt;
    },

    scaleContainers() {
      const bodyHeight = document.body.offsetHeight;
      const desktopHeight = 960;
      return bodyHeight <= 700 ? bodyHeight / desktopHeight : 1;
    },

    /**
     * due to the way webpack works (only dist folder is used in published version of the app)
     * we import the local images, instead of giving the relative path, and use the
     * generated link "/dist/localImageName.extension?generated code"
     * also we create a separate div for smiley background image so it will not conflict
     * with the gradient background color of the container
     * */
    setSmiley(containerClass) {
      const url = `url(${smiley})`;
      const containerDiv = document.getElementsByClassName(containerClass)[0];
      const smileyCt = document.createElement('div');
      smileyCt.style.width = '18px';
      smileyCt.style.height = '18px';
      smileyCt.style.margin = '5px';
      smileyCt.style.backgroundImage = url;
      containerDiv.appendChild(smileyCt);
    },

    adjustContainers() {
      const isSmallScaleContainer = this.scaleContainers() < 1;
      const scaleParam = Math.round(this.scaleContainers() * 100) / 100;

      TweenMax.set('.sample-container', {
        css: {
          height: this.ctHeight * scaleParam + (isSmallScaleContainer ? 20 : 0),
          width: this.ctWidth * scaleParam
        }
      });
      TweenMax.set('.exercise-container', {
        css: {
          height: this.ctHeight * scaleParam + (isSmallScaleContainer ? 20 : 0),
          width: this.ctWidth * scaleParam
        }
      });
    },

    // we create wood images, non draggable, for Demo animation
    injectDemoShapes() {
      const self = this;
      this.currentRound.steps.forEach((shape, i) => {
        const shapeCt = document.createElement('div');
        shapeCt.style.display = 'inline-block';
        shapeCt.style.zIndex = 2;
        shapeCt.setAttribute('id', self.getShapeCtDemoId(i));
        shapeCt.setAttribute('blockType', shape.stroke.toLowerCase());

        const img = self.createImage(shape, i, true);

        TweenMax.set(img, { css: { autoAlpha: 0 } });
        shapeCt.appendChild(img);
        self.$refs.woodsContainer.appendChild(shapeCt);
      });
      TweenMax.delayedCall(0.2, () => {
        self.fitDemoImagesToCt();
      });
    },
    fitDemoImagesToCt() {
      const self = this;

      const tlInject = new TimelineLite({
        onComplete: self.moveShapesToDemoContainer,
        onCompleteParams: ['sample-container']
      });

      const shapes = self.$refs.woodsContainer.children;
      const shapesArray = [].slice.call(shapes);

      shapesArray.forEach(shapeCt => {
        const img = shapeCt.children[0];

        self.fitToCt(shapeCt, img, true);
        tlInject.set(img, {
          css: { autoAlpha: 1 },
          onStart() {}
        });
      });
    },

    getLabel(txt) {
      return txt.split(/(?=[A-Z])/).join(' ');
    },

    getDuplicateElement(arr) {
      let multiple = [];
      if (arr.length) {
        multiple = arr
          .map(e => e.stroke)
          .map((e, i, final) => final.indexOf(e) !== i && i)
          .filter(obj => arr[obj])
          .map(e => arr[e].stroke);
      }
      return multiple.length && multiple[0];
    },

    setLabel(baseText, label) {
      // set correct aria-label based on number of same elements with the same labels
      // eg: first big line, second big line etc.
      let duplicatedLabel;
      if (label) duplicatedLabel = this.getLabel(label);
      let count = 0;
      const order = {
        1: 'first',
        2: 'second',
        3: 'third',
        4: 'fourth'
      };
      return function(receivedLabel) {
        if (duplicatedLabel === receivedLabel) {
          count += 1;
          return `${baseText} ${order[count]} ${receivedLabel}`;
        }
        return `${baseText} ${receivedLabel}`;
      };
    },

    // we create wood images, draggable, for exercise
    injectExerciseShapes() {
      const self = this;
      const { isMobile } = this.userDevice;
      const tLite = new TimelineLite();
      const parentContainer = isMobile
        ? document.getElementById('dragging-container')
        : document.getElementById('activity-boundary');
      let hitBounds = null;
      const duplicatedElement = this.getDuplicateElement(this.currentRound.steps);

      const theLabel = this.setLabel('Wood Piece for', duplicatedElement);
      const theDropabbleLabel = this.setLabel('Droppable area for', duplicatedElement);

      this.currentRound.steps.forEach((shape, i) => {
        let dragTimer = 0;
        let hasHitBoundary = false;
        this.scaleParam = Math.round(this.scaleContainers() * 100) / 100;
        const img = self.createImage(shape, i, false);

        const getLabel = this.getLabel(shape.stroke);

        const shapeCt = document.createElement('div');
        shapeCt.style.display = 'inline-block';
        shapeCt.style.zIndex = 2;
        shapeCt.setAttribute('id', self.getShapeCtExerciseId(i));
        const button = document.createElement('button');
        button.setAttribute('aria-pressed', false);
        button.setAttribute('aria-label', theLabel(getLabel));
        button.setAttribute('id', this.getExerciseImageId(i));

        Object.assign(button.style, {
          backgroundImage: `url(${shape.image})`,
          backgroundColor: 'transparent',
          backgroundSize: 'cover',
          border: 'none',
          transformOrigin: '0% 0%',
          transform: `rotate(${shape.position.r}deg)`
        });

        TweenMax.delayedCall(0.1, () => {
          Object.assign(button.style, {
            width: `${img.naturalWidth * this.scaleParam}px`,
            height: `${img.naturalHeight * this.scaleParam}px`
          });
        });
        const droppableCt = this.drawDroppableArea(shape, i, img, theDropabbleLabel);
        TweenMax.set(button, { css: { autoAlpha: 0 } });
        shapeCt.appendChild(button);
        // play ding sound
        self.$refs.exerciseWoodsContainer.appendChild(shapeCt);
        self.$refs.exerciseContainer.appendChild(droppableCt);

        // make selected shape draggable
        this.draggableInstances.push(
          Draggable.create(button, {
            cursor: 'grab',
            type: 'x, y',
            shapeName: shape.stroke,
            bounds: parentContainer,
            edgeResistance: 1.1,
            onPress() {
              if (hitBounds) return;
              hitBounds = getActivityBoundings();
            },
            onDrag() {
              // work-around for iOS not firing touchend on tablet
              if (!isMobile) return;
              clearTimeout(dragTimer);
              const { pointerX, pointerY } = this;
              const hasTouched = hasTouchedBoundings(pointerX, pointerY, hitBounds);
              if (hasTouched && !hasHitBoundary) {
                dragTimer = setTimeout(() => {
                  hasHitBoundary = true;
                  self.handleDrop(this, this.target);
                }, 200);
              }
            },
            onDragStart() {
              tLite.set(button.style, { cursor: 'grabbing' });
            },
            onDragEnd() {
              clearTimeout(dragTimer);
              hasHitBoundary = false;
              self.handleDrop(this);
            }
          })
        );

        button.addEventListener('keydown', this.selectStroke);
      });

      // we need this delay so dom elements have proper new coordinates after si
      // injection and rendering
      TweenMax.delayedCall(0.2, () => {
        self.fitExerciseImagesToCt();
      });
      // we delay the display, to wait for fitToCt() on each image to fulfill
      TweenMax.delayedCall(1, () => {
        self.displayExerciseShapes(0);
      });
    },
    fitExerciseImagesToCt() {
      const self = this;

      const shapes = self.$refs.exerciseWoodsContainer.children;
      const shapesArray = [].slice.call(shapes);

      shapesArray.forEach(shapeCt => {
        const img = shapeCt.children[0];
        self.fitToCt(shapeCt, img, false);
      });
    },
    createImage(shape, i, isDemo) {
      const img = new Image();
      const scaleParam = Math.round(this.scaleContainers() * 100) / 100;

      img.src = this.getImagePath(shape.stroke);
      if (isDemo) {
        img.setAttribute('id', this.getDemoImageId(i));
        img.draggable = false;
      } else {
        img.setAttribute('id', this.getExerciseImageId(i));
      }

      img.style.transformOrigin = '0% 0%';
      img.style.transform = `rotate(${shape.position.r}deg)`;

      img.style.height = '100%';

      TweenMax.delayedCall(0.1, () => {
        img.style.width = `${img.naturalWidth * scaleParam}px`;
        img.style.height = `${img.naturalHeight * scaleParam}px`;
      });

      return img;
    },
    getImagePath(blockType) {
      switch (blockType) {
        case 'BigLine':
          return bigLine;
        case 'LittleLine':
          return littleLine;
        case 'BigCurve':
          return bigCurve;
        case 'LittleCurve':
          return littleCurve;
        default:
          return '';
      }
    },

    /**
     * for exercise, we make shapes visible one by one, joind by a ding sound
     * attention: recursive method
     * */
    displayExerciseShapes(currentIndex) {
      const currentImg = document.getElementById(this.getExerciseImageId(currentIndex));
      // if no more shapes => stop recursivity
      if (!currentImg) return;
      const self = this;
      Howler.volume(this.soundMuted ? 0 : this.soundVolume); // eslint-disable-line no-undef
      self.sndBell();
      TweenMax.set(currentImg, { css: { autoAlpha: 1 } });

      const label = currentImg.getAttribute('aria-label') + ' added';
      self.handleAriaLive(label);
      // eslint-disable-next-line
      currentIndex += 1;
      // display next exercise woodshape and play a spound, with a delay of one second
      // using Timeline to display each shape and play a bell is not working using Timeline,
      // so I will use delayedCall
      // [INFO] Raluca : http://greensock.com/forums/topic/3413-onstart-onstartparams/
      TweenMax.delayedCall(1.2, () => {
        self.displayExerciseShapes(currentIndex);
      });
    },

    /**
     * when the image is created and injected in DOM,
     * after ROTATION around the top-left corner, the image will go out of the container
     * where initially was placed, so we move it back to shapeCt
     * */
    fitToCt(shapeCt, img) {
      const divEl = shapeCt;
      const imgEl = document.getElementById(img.id);

      if (imgEl.getBoundingClientRect().left < divEl.getBoundingClientRect().left) {
        imgEl.setAttribute(
          'fitOffsetX',
          divEl.getBoundingClientRect().left - imgEl.getBoundingClientRect().left
        );
        TweenMax.set(imgEl, {
          css: {
            x: divEl.getBoundingClientRect().left - imgEl.getBoundingClientRect().left
          }
        });
      } else {
        imgEl.setAttribute('fitOffsetX', 0);
      }

      if (imgEl.getBoundingClientRect().top < divEl.getBoundingClientRect().top) {
        imgEl.setAttribute(
          'fitOffsetY',
          divEl.getBoundingClientRect().top - imgEl.getBoundingClientRect().top
        );
        TweenMax.set(imgEl, {
          css: {
            y: divEl.getBoundingClientRect().top - imgEl.getBoundingClientRect().top
          }
        });
      } else {
        imgEl.setAttribute('fitOffsetY', 0);
      }
    },

    /**
     * at Demo, we take injected demo shapes (the one created in injectDemoShapes())
     * and animate them to Demo container
     * */
    moveShapesToDemoContainer(targetContainerClass) {
      const self = this;
      const { soundsBucket, captionsBucket, accessibilityMode } = this;
      const scaleParam = Math.round(this.scaleContainers() * 100) / 100;
      const margins = {
        top: scaleParam < 1 ? 20 : 0
      };

      // move shapes to sample container
      const targetCoords = self.getTargetCoordinates(targetContainerClass);
      const tl = new TimelineLite({
        onComplete() {
          self.moveCtsLeft();
          self.playYourTurn();
        }
      });

      this.currentRound.steps.forEach(({ position, stroke, stepIndex }) => {
        const wood = document.getElementById(this.getDemoImageId(stepIndex));
        const sourceCoords = self.getPositionForElement(document.getElementById(wood.id));
        const scaledX = position.x * scaleParam;
        const scaledY = position.y * scaleParam;
        const translateCoords = {
          x: targetCoords.left - sourceCoords.left,
          y: targetCoords.top - sourceCoords.top
        };

        const tween = tl.to(wood, 2, {
          css: {
            x: translateCoords.x + scaledX,
            y: translateCoords.y + scaledY + margins.top
          },
          delay: 0.1,
          onStart() {
            self.setCurrentTweenData(stepIndex + 1);
            const strokeSound = soundsBucket.lineSounds.find(sound => sound[stroke]);
            const strokeCaption = captionsBucket.lineText.find(caption => caption[stroke]);

            self.playSound(strokeSound[stroke], strokeCaption[stroke], accessibilityMode);
          }
        });
        this.setOnGoingAnimation(tween);
      });
      this.setCurrentTweenData(1);
    },

    /**
     * when the user puts the shape in the correct position, we set the next current shape
     * if it was the last shape from letter, we move to next letter
     * if it was the last letter, we complete activity
     * */
    nextShape() {
      const self = this;
      this.currentShape += 1;
      if (this.currentShape === this.currentRound.steps.length) {
        // play congratulations sound for current letter
        this.playStepCongrats();
        this.draggableInstances = [];
        if (this.currentRound.roundIndex < this.rounds.length - 1) {
          this.nextLetter();
        } else {
          // we deplay call, to wait for end round congratulations to finish
          this.errorLoggerStop();
          TweenMax.delayedCall(2.5, () => {
            self.winGame();
          });
        }
      }
    },

    /**
     * when the user finish to build one letter, we change the UI
     * (containers, wood shapes) for next letter,
     * and start the Demo for next letter
     * */
    nextLetter() {
      const self = this;

      TweenMax.to('.activity-stage', 2.5, {
        css: { autoAlpha: 0 },
        onComplete() {
          self.setupNewRound();

          self.currentShape = 0;
          self.$refs.exerciseWoodsContainer.innerHTML = '';
          self.$refs.woodsContainer.innerHTML = '';
          self.$refs.sampleContainer.innerHTML = '';
          self.$refs.exerciseContainer.innerHTML = '';

          self.startDemo();
        }
      });
    },

    /**
     * move demo container to the left after demo animation is finished
     * */
    moveCtsLeft() {
      const self = this;
      const tl = new TimelineLite({
        onComplete() {
          self.injectExerciseShapes();
        }
      });
      this.setOnGoingAnimation(tl);
      TweenMax.to('.sample-container', 1, { css: { x: 0 } });
      TweenMax.staggerTo(this.$refs.woodsContainer.children, 1, { css: { x: -150 } }, '-=1');
      tl.to('.exercise-container', 1, { css: { autoAlpha: 1 } });
      this.setOnGoingAnimation(null);
      this.setAnimationsMode(true);
    },

    getPositionForElement(el) {
      return {
        left: el.getBoundingClientRect().left,
        top: el.getBoundingClientRect().top
      };
    },

    getTargetCoordinates(className) {
      const el = document.querySelector(`.${className}`);
      return {
        left: el.getBoundingClientRect().left,
        top: el.getBoundingClientRect().top
      };
    },

    handleDrop(dragContext) {
      // check if it's the right currentShape
      if (this.isCurrentShape(dragContext.target) && this.isInCorrectPosition(dragContext.target)) {
        dragContext.kill();
        Howler.volume(this.soundMuted ? 0 : this.soundVolume); // eslint-disable-line no-undef
        this.sndBell();
        this.handleAriaLive('Good Job!');
        this.moveShapeToFinalPosition(dragContext.target);
        this.nextShape();
      } else {
        this.sndError();
        this.handleAriaLive('Try Again!');
        this.logError(dragContext.vars.shapeName, ' ', 'DNDplace');
        const fitOffsetX = dragContext.target.attributes.fitOffsetX.value;
        const fitOffsetY = dragContext.target.attributes.fitOffsetY.value;
        TweenMax.to(dragContext.target, 0.5, { css: { x: fitOffsetX, y: fitOffsetY } });
      }
    },
    /**
     * when wood was dropped in an approximately correct position,
     * we move(animate) it into EXACT correct position in Exercise container
     * */
    moveShapeToFinalPosition(target) {
      const wood = document.getElementById(this.getExerciseImageId(this.currentShape));
      const initialPosition = this.getInitialPosition();
      const finalPosition = this.getFinalPosition();
      const currentPosition = this.getCurrentPosition(target);

      const translateCoordsTo = {
        x: finalPosition.left - initialPosition.left,
        y: finalPosition.top - initialPosition.top
      };
      const translateCoordsFrom = {
        x: currentPosition.left - initialPosition.left,
        y: currentPosition.top - initialPosition.top
      };

      TweenMax.fromTo(
        wood,
        0.15,
        { css: { x: translateCoordsFrom.x, y: translateCoordsFrom.y } },
        { css: { x: translateCoordsTo.x, y: translateCoordsTo.y } }
      );
    },

    isCurrentShape(target) {
      return target.id === this.getExerciseImageId(this.currentShape);
    },

    isInCorrectPosition(target) {
      const finalPosition = this.getFinalPosition();
      const currentPos = this.getCurrentPosition(target);
      const translateCoords = {
        x: currentPos.left - finalPosition.left,
        y: currentPos.top - finalPosition.top
      };
      return (
        translateCoords.x < 50 &&
        translateCoords.x > -50 &&
        translateCoords.y < 50 &&
        translateCoords.y > -50
      );
    },

    getInitialPosition() {
      const currentShapeCtId = this.getShapeCtExerciseId(this.currentShape);
      const currentShape = document.getElementById(currentShapeCtId);
      return {
        left: currentShape.getBoundingClientRect().left,
        top: currentShape.getBoundingClientRect().top
      };
    },
    getCurrentPosition(target) {
      return {
        left:
          target.getBoundingClientRect().left + parseInt(target.attributes.fitOffsetX.value, 10),
        top: target.getBoundingClientRect().top + parseInt(target.attributes.fitOffsetY.value, 10)
      };
    },

    getFinalPosition() {
      const scaleParam = Math.round(this.scaleContainers() * 100) / 100;
      const margins = {
        top: scaleParam < 1 ? 20 : 0
      };
      const shape = this.currentRound.steps[this.currentShape];
      const { position } = shape;
      const ctCoords = this.getTargetCoordinates('exercise-container');
      const scaledX = position.x * scaleParam;
      const scaledY = position.y * scaleParam;
      return {
        left: ctCoords.left + scaledX,
        top: ctCoords.top + scaledY + margins.top
      };
    },

    winGame() {
      const self = this;
      // fade out all containers
      TweenMax.staggerTo(
        [
          '.sample-container',
          '.exercise-container',
          '.woods-container',
          '.exercise-woods-container'
        ],
        1,
        { css: { autoAlpha: 0 } }
      );
      TweenMax.delayedCall(2, () => {
        self.completeActivity();
      });
    },

    playCongrats() {
      const { soundsBucket, captionsBucket, accessibilityMode } = this;
      this.playSound(
        soundsBucket.general.generalCongrats,
        captionsBucket.general.generalCongrats,
        accessibilityMode
      );
    },

    blurElement(element) {
      element.blur();
      element.setAttribute('tabindex', '-1');
      element.setAttribute('aria-pressed', false);
      element.classList.remove('highlight');
    },

    isPressed(index) {
      const button = this.$refs.exerciseWoodsContainer.children[index].children[0];
      return button.getAttribute('aria-pressed') === 'true';
    },
    togglePressed(id) {
      const index = id.split('-')[1];
      const pressed = this.isPressed(index);
      const stroke = this.$refs.exerciseWoodsContainer.children[index].children[0];
      if (!pressed) {
        stroke.classList.add('highlight');
      } else {
        stroke.classList.remove('highlight');
      }
      this.handleHighlightDroppables(!pressed);
      stroke.setAttribute('aria-pressed', !pressed);
    },
    handleHighlightDroppables(highLight) {
      const droppableCts = Array.from(this.$refs.exerciseContainer.children);
      const availableSpots = droppableCts.filter(ct => ct.getAttribute('tabIndex') !== '-1');

      Array.from(availableSpots).forEach(spot =>
        highLight ? spot.classList.add('highlight') : spot.classList.remove('highlight')
      );
    },
    selectStroke(event) {
      const shapeId = event.target.id;
      const index = shapeId.split('-')[1];
      if (event.key === 'Enter') {
        this.togglePressed(shapeId);
        if (this.isPressed(index)) {
          // eslint-disable-next-line prefer-destructuring
          this.selectedStrokeNr = shapeId.split('-')[1];
          if (this.selectedStroke) {
            this.selectedStroke.setAttribute('aria-pressed', false);
            this.selectedStroke.classList.remove('highlight');
          }
          this.selectedStroke = document.getElementById(shapeId);
        } else {
          this.selectedStrokeNr = null;
          this.selectedStroke = null;
        }
      }
    },
    handleDropWithKeyboard(event) {
      const droppableSlotNr = event.target.id.split('-')[1];
      if (event.key === 'Enter' && this.selectedStroke) {
        if (this.isCurrentShape(this.selectedStroke) && droppableSlotNr === this.selectedStrokeNr) {
          this.handleHighlightDroppables(false);
          this.moveShapeToFinalPosition(this.selectedStroke);
          this.sndBell();
          this.handleAriaLive('Good job!');
          this.blurElement(this.selectedStroke);
          this.blurElement(event.target);
          this.nextShape();
        } else {
          this.sndError();
          this.handleAriaLive('Try again!');
        }
      }
    },

    getDemoImageId(i) {
      return `imgDemo${i}`;
    },

    getExerciseImageId(i) {
      return `imgExercise-${i}`;
    },

    getShapeCtDemoId(i) {
      return `shapeCtDemo${i}`;
    },

    getShapeCtExerciseId(i) {
      return `shapeCtExercise${i}`;
    }
  }
};
</script>

<style lang="css" scoped>
@media only screen and (min-device-width: 700px) and (max-device-width: 1024px) {
  #dragging-container {
    width: 99%;
    height: 99%;
  }
}
</style>
